import CustomerJson, { validateGroupBookingCustomerJson, validateGroupBookingCustomerJsonAddNew } from "./CustomerJson";
import { GroupBookingStatus } from "./GroupBookingStatus";
import SepaMandateJson, { validateSepaMandate, validateSepaMandateAddNew } from "./SepaMandateJson";
import InvoiceInfoJson from "./InvoiceInfoJson";
import { Validation } from "./Validation";


export default interface GroupBookingJson {
   ID: number | null;

   /**
    * Datum und Uhrzeit an dem der Gruppenbesuch vom Kunden ursprünglich im Formular gewünscht wurde.
    */
   desiredDateTime: string;

   /**
    * Datum und Uhrzeit an dem der Gruppenbesuch ausgeführt werden soll.
    */
   beginDateTime: string;

   /**
    * Datum und Uhrzeit an dem der Gruppenbesuch endet.
    */
   endDateTime: string;

   /**
    * Freitextfeld für Alternativtermine und Buchungswünsche etc. (Anfrage vom Kunden)
    */
   remarks: string;

   /**
    * Gibt an, ob in dem Angebot ein Mittagessen enthalten sein soll.
    */
   isLunchIncluded: boolean,

   /**
    * Gibt die Anzahl an Portionen fürs Mittagessen an. Kann nur null sein, wenn isLunchIncluded auf false ist.
    */
   lunchPortionAmount: number | null;

   /**
    * Gibt an, ob das Angebot für einen Ganzen Tag gelten soll oder nur einen halben.
    */
   isWholeDay: boolean,

   /**
    * Mitarbeiter der diese Gruppenbuchung betreut (Wenn einer benötigt wird)
    */
   staffId: number | null;

   /**
    * Kunde für diesen Gruppenbesuch.
    */
   customer: CustomerJson;

   /**
    * Status der Gruppenbesuchbuchung
    */
   state: GroupBookingStatus;

   /**
    * Titel der ausgewählten Programmbausteine. (Kurztext für Verwaltung)
    */
   programTitle: string;

   /**
    * Detaillierte Beschreibung der ausgewählten Programmbausteine. (Angebot der Verwaltung)
    */
   programDescription: string;

   /**
    * Angabe der Betreuer, wie viele Kinder kommen werden.
    */
   estimatedChildrenCount: number;

   /**
    * Angabe der Betreuer, wie alt die Kinder sein werden als Textfeld.
    */
   estimatedChildrenAge: string;

   /**
    * Preis zu dem der Gruppenbesuch angeboten wird in Eurocent. Kann null sein wenn der Gruppenbesuch noch in Planung ist.
    */
   price: number | null;

   /**
    * Kosten die bei einer Stornierung anfallen in Eurocent.
    */
   cancellationFee: number;

   /**
    * Stornierungsbedingungen.
    */
   cancellationRemarks: string;

   /**
    * Stornierungsbegründung. Sowohl für Kunden als auch für Verwaltung, je nach Status (confirm or cancel).
    */
   cancellationReason: string;

   /**
    * SEPA-Lastschriftmandat für die Zahlung. Kann auch null sein, wenn eine Bar oder Rechnungszahlung gewünscht ist.
    */
   sepaMandate: SepaMandateJson | null;

   /**
     * Rechnungsinformationen wie Rechnungsnummer.
     */
   invoiceInfos: InvoiceInfoJson[] | null;
}

export type GroupBookingCustomerViewJson = Omit<GroupBookingJson, "staffId" | "sepaMandate" | "invoiceInfos"> & { "canStillBeModified": boolean; };

export function validateGroupBookingJsonManagement(item: GroupBookingJson) {
   return new Validation(item)
      .validateInteger("ID", 0)
      .validateDate("desiredDateTime")
      .validateDate("beginDateTime")
      .validateDateLater("endDateTime", "beginDateTime")
      .validateString("remarks", 1000)
      .validateInteger("staffId", 0, null, true)
      .validateUsingFunction("customer", validateGroupBookingCustomerJson, true)
      .validateInteger("estimatedChildrenCount", 1, 150)
      .validateInteger("lunchPortionAmount", 1, 500, !item.isLunchIncluded)
      .validateString("programTitle", 200)
      .validateString("programDescription", null)
      .validateNonEmptyString("estimatedChildrenAge", 200)
      .validateInteger("price", 0, null, true)
      .validateInteger("cancellationFee", 0)
      .validateString("cancellationRemarks", 500)
      .validateString("cancellationReason", 1000)
      .validateUsingFunction("sepaMandate", validateSepaMandate, true)
      .finish();
}

export function validateGroupBookingJsonManagementReadyToOffer(item: GroupBookingJson) {
   return new Validation(item)
      .validateInteger("ID", 0)
      .validateDate("desiredDateTime")
      .validateDate("beginDateTime")
      .validateDateLater("endDateTime", "beginDateTime")
      .validateString("remarks", 1000)
      .validateInteger("staffId", 0, null, true)
      .validateUsingFunction("customer", validateGroupBookingCustomerJson, true)
      .validateInteger("estimatedChildrenCount", 1, 150)
      .validateInteger("lunchPortionAmount", 1, 500, !item.isLunchIncluded)
      .validateNonEmptyString("programTitle", 200)
      .validateNonEmptyString("programDescription", null)
      .validateNonEmptyString("estimatedChildrenAge", 200)
      .validateInteger("price", 0, null)
      .validateInteger("cancellationFee", 0)
      .validateString("cancellationRemarks", 500)
      .validateString("cancellationReason", 1000)
      .validateUsingFunction("sepaMandate", validateSepaMandate, true)
      .finish();
}

export function validateGroupBookingJsonManagementAdd(item: GroupBookingJson) {
   return new Validation(item)
      .validateEquals("ID", null)
      .validateDate("desiredDateTime")
      .validateDate("beginDateTime")
      .validateDateLater("endDateTime", "beginDateTime")
      .validateString("remarks", 1000)
      .validateInteger("staffId", 0, null, true)
      .validateUsingFunction("customer", validateGroupBookingCustomerJson, true)
      .validateInteger("estimatedChildrenCount", 1, 150)
      .validateInteger("lunchPortionAmount", 1, 500, !item.isLunchIncluded)
      .validateString("programTitle", 200)
      .validateString("programDescription", null)
      .validateNonEmptyString("estimatedChildrenAge", 200)
      .validateInteger("price", 0, null, true)
      .validateInteger("cancellationFee", 0)
      .validateString("cancellationRemarks", 500)
      .validateString("cancellationReason", 1000)
      .validateUsingFunction("sepaMandate", validateSepaMandate, true)
      .finish();
}

export function validateGroupBookingJsonBookingForm(item: GroupBookingJson, groupBookingCanRequestUntilDaysAhead: number) {
   return new Validation(item)
      .validateEquals("ID", null)  
      .validateFutureDate("desiredDateTime", groupBookingCanRequestUntilDaysAhead)
      .validateFutureDate("beginDateTime", groupBookingCanRequestUntilDaysAhead)
      .validateDateLater("endDateTime", "beginDateTime")
      .validateNonEmptyString("remarks", 1000)
      .validateEquals("staffId", null)
      .validateUsingFunction("customer", validateGroupBookingCustomerJsonAddNew, true)
      .validateInteger("estimatedChildrenCount", 1, 150)
      .validateInteger("lunchPortionAmount", 1, 500, !item.isLunchIncluded)
      .validateEquals("programTitle", "")
      .validateEquals("programDescription", "")
      .validateNonEmptyString("estimatedChildrenAge", 200)
      .validateEquals("price", 0,)
      .validateInteger("cancellationFee", 0)
      .validateString("cancellationRemarks", 500)
      .validateString("cancellationReason", 1000)
      .validateUsingFunction("sepaMandate", validateSepaMandateAddNew, true)
      .validateEquals("state", GroupBookingStatus.requested)
      .finish();
}

export function validateGroupBookingJsonCancellation(item: GroupBookingCustomerViewJson) {
   return new Validation(item)
      .validateInteger("ID", 0)
      .validateNonEmptyString("cancellationReason", 1000)
      .validateEquals("state", GroupBookingStatus.offerToCustomer)
      .finish();
}

export function getGroupBookingRequestMinDate(groupBookingCanRequestUntilDaysAhead: number): Date {
   var stopDate = new Date();
   stopDate.setDate(stopDate.getDate() + groupBookingCanRequestUntilDaysAhead + 1);
   stopDate.setHours(0, 0, 0);
   return stopDate;
}